/**
 * ExportMenu: "Export" button with a small menu of file formats (PDF report,
 * CSV summary, block equivalency file, shapefile). Used for district plans
 * and communities of interest; the caller supplies `onExport` and which
 * formats are currently unavailable (with a reason shown in the menu).
 */
import { useEffect, useRef, useState } from 'react';
import { EXPORT_FORMATS, type ExportFormat } from '../lib/exports';
import { ChevronDownIcon } from './icons';

const DESCRIPTIONS: Record<ExportFormat, string> = {
  pdf: 'Printable map and summary',
  csv: 'Totals for spreadsheets',
  equivalency: 'Block-by-block list (GEOID20)',
  shapefile: 'Boundaries for GIS (zipped .shp)',
};

function DownloadIcon(): JSX.Element {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
    </svg>
  );
}

export function ExportMenu({
  onExport,
  unavailable = {},
  className = '',
  label = 'Export',
  align = 'left',
  variant = 'ghost',
  block = false,
}: {
  onExport: (format: ExportFormat) => Promise<void>;
  /** Formats that can't be exported right now, with the reason. */
  unavailable?: Partial<Record<ExportFormat, string>>;
  className?: string;
  label?: string;
  align?: 'left' | 'right';
  /** ghost: full-width row in a panel; secondary: standalone outlined button. */
  variant?: 'ghost' | 'secondary';
  /** Secondary variant only: stretch the button to fill its container. */
  block?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: PointerEvent): void {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  async function run(format: ExportFormat): Promise<void> {
    setBusy(format);
    setError(null);
    try {
      await onExport(format);
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The export failed.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={
          variant === 'ghost'
            ? 'btn-ghost w-full justify-start'
            : `btn-secondary${block ? ' w-full justify-center' : ''}`
        }
      >
        <DownloadIcon />
        {busy ? 'Preparing…' : label}
        <ChevronDownIcon size={14} className={variant === 'ghost' ? 'ml-auto' : ''} />
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Export format"
          className={`floating absolute top-full z-40 mt-1 w-64 p-1 ${align === 'right' ? 'right-0' : 'left-0'}`}
        >
          {EXPORT_FORMATS.map(({ format, label: name, ext }) => {
            const reason = unavailable[format];
            return (
              <button
                key={format}
                type="button"
                role="menuitem"
                disabled={!!reason || busy !== null}
                onClick={() => void run(format)}
                title={reason}
                className="flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left hover:bg-stone-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
              >
                <span className="mt-0.5 rounded bg-stone-100 px-1 font-mono text-[10px] font-bold uppercase text-stone-500">
                  {ext}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-ink">
                    {busy === format ? 'Preparing…' : name}
                  </span>
                  <span className="block text-[11px] leading-snug text-stone-500">
                    {reason ?? DESCRIPTIONS[format]}
                  </span>
                </span>
              </button>
            );
          })}
          {error && (
            <p className="m-1 rounded-lg bg-red-50 px-2 py-1.5 text-[11px] text-red-700" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
